import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { ReportSettingsFragmentProps } from './ReportSettingsFragment.props';
import { useReportSettingsForm } from './useReportSettingsForm';
import { ColorProps } from '../../../../../@types';
import {
  FormControlLabel,
  FormControlLabelPlacementProps,
} from '../../../../../components/atoms/FormControlLabel';
import { Switch } from '../../../../../components/atoms/Switch';
import { BrandSelector } from '../../../../../components/molecules/BrandSelector';
import { SelectionButton } from '../../../../../components/molecules/SelectionButton';
import { ReportEditConfirmDialog } from '../ReportEditFragment/ReportEditConfirmDialog';
import {
  FIELD_HINT_SX,
  FORM_STACK_SX,
  SECTION_HEADING_SX,
  TAB_BODY_SX,
} from '../ReportEditFragment/reportEditDialog.styles';
import { ReportEditDialogFooter } from '../ReportEditFragment/ReportEditDialogFooter';

export const ReportSettingsFragment: React.FC<ReportSettingsFragmentProps> = ({
  reportCode,
  permissions,
}) => {
  const form = useReportSettingsForm(reportCode, permissions);
  const { obfuscation } = form.reportSettings;

  return (
    <>
      <Box sx={TAB_BODY_SX}>
        <Stack spacing={2.5} sx={FORM_STACK_SX}>
          {form.canObfuscate ? (
            <Box component="section">
              <Typography sx={SECTION_HEADING_SX}>Report Obfuscation</Typography>
              <Stack spacing={1.75} sx={{ mt: 1 }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  labelPlacement={FormControlLabelPlacementProps.End}
                  control={
                    <Switch
                      color={ColorProps.Primary}
                      checked={obfuscation.obfuscate}
                      onChange={(event) => form.onToggleObfuscate(event.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '13px' }}>
                      Show ALL part locations but obfuscate non-Client location names
                    </Typography>
                  }
                />
                <Box>
                  <SelectionButton
                    label="Client Selection *"
                    error={form.errors.hasNoClientHierarchy}
                    value={obfuscation.clientHierarchy}
                    onClick={form.onOpenPicker}
                  />
                  <Typography variant="caption" sx={FIELD_HINT_SX}>
                    Locations outside this hierarchy have their names hidden.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: 'v4.textMuted' }}>
              There are no settings available to you for this report.
            </Typography>
          )}
        </Stack>
      </Box>

      <ReportEditDialogFooter
        primaryLabel="Save settings"
        onPrimary={form.handleSave}
        primaryDisabled={form.isSaving}
      />

      <BrandSelector
        open={form.isPickerOpen}
        onChangeNodeType={form.onChangeNodeType}
        handleClose={form.onClosePicker}
        handleParentSelection={form.onSelectHierarchy}
        isActiveClear={false}
        clients={form.clients}
        contracts={form.contracts}
        brands={form.brands}
        clientId={parseInt(obfuscation.selectedClients[0])}
        contractId={parseInt(obfuscation.selectedContracts[0])}
        brandId={parseInt(obfuscation.selectedBrands[0])}
        pageType="report-settings"
      />
      <ReportEditConfirmDialog
        open={form.showObfuscateWarning}
        title="Obfuscate location names?"
        description="Confirm report parameters are correct prior to sending to any external parties. Enabling this option may allow for visibility of locations outside the client's hierarchy."
        confirmLabel="Enable obfuscation"
        onCancel={form.onCancelObfuscate}
        onConfirm={form.onConfirmObfuscate}
      />
    </>
  );
};
